import { useMutation } from '@apollo/client';
import type { ParentType } from '@risksmart-app/domain/src/types/consts/index';
import {
  InsertFormFieldPositionsDocument,
  UpdateFormFieldPositionsDocument,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { useCallback } from 'react';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import { useUpdateFormFieldPositionsTRPC } from './useUpdateFormFieldPositionsTRPC';

export type UpdateFormFieldPositionsInput = {
  parentType: ParentType;
  ordering: { FieldId: string; Position: number }[];
  // Whether the form_configuration row already exists. Drives the Apollo
  // insert-vs-update document choice (the insert variant creates the config
  // row via its nested upsert when missing). The tRPC path is a single
  // idempotent upsert and ignores it.
  // no-dd-sa:typescript-best-practices/boolean-prop-naming
  isUpdate: boolean;
};

/**
 * Public flag-gated wrapper for the ordering-only form save. Consumers import
 * THIS — never the `*TRPC` twin directly. With the `trpc` flag off it runs the
 * legacy Apollo insert/update position mutations; with it on it calls the
 * single idempotent tRPC `updateFieldOrdering` procedure.
 */
export const useUpdateFormFieldPositions = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [updateFormFieldPositionsGraphQL, updateState] = useMutation(
    UpdateFormFieldPositionsDocument,
    {
      update: (cache) => {
        evictField(cache, 'form_configuration');
        evictField(cache, 'form_field_ordering');
      },
    }
  );
  const [insertFormFieldPositionsGraphQL, insertState] = useMutation(
    InsertFormFieldPositionsDocument,
    {
      update: (cache) => {
        evictField(cache, 'form_configuration');
        evictField(cache, 'form_field_ordering');
      },
    }
  );

  const trpcMutation = useUpdateFormFieldPositionsTRPC();
  const { updateFormFieldPositions: updateFieldPositionsTRPC } = trpcMutation;

  // Memoise so the returned function is referentially stable. The consumer
  // (`CustomisableFieldWrapper`) feeds it into `saveFields = useCallback(...)`
  // then registers that via `setOnSave` in an effect keyed on `[saveFields]`;
  // an unstable function here makes `saveFields` new every render, so the
  // effect re-fires and setState-loops (React #185) on entering edit mode.
  // The Apollo mutate fns and the memoised tRPC callback are all stable refs.
  const updateFormFieldPositions = useCallback(
    async ({
      parentType,
      ordering,
      isUpdate,
    }: UpdateFormFieldPositionsInput): Promise<void> => {
      if (trpcEnabled) {
        await updateFieldPositionsTRPC({
          ParentType: parentType,
          Ordering: ordering,
        });

        return;
      }

      const mutateFn = isUpdate
        ? updateFormFieldPositionsGraphQL
        : insertFormFieldPositionsGraphQL;

      await mutateFn({
        variables: {
          fieldConfig: ordering.map((row) => ({
            FormConfigurationParentType: isUpdate ? parentType : undefined,
            FieldId: row.FieldId,
            Position: row.Position,
            form: null,
          })),
          fieldIds: ordering.map((row) => row.FieldId),
          parentType,
        },
      });
    },
    [
      trpcEnabled,
      updateFieldPositionsTRPC,
      updateFormFieldPositionsGraphQL,
      insertFormFieldPositionsGraphQL,
    ]
  );

  if (trpcEnabled) {
    return {
      updateFormFieldPositions,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    updateFormFieldPositions,
    loading: updateState.loading || insertState.loading,
    error: updateState.error ?? insertState.error,
  };
};
